import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UserPlus, Users, GraduationCap, ShieldCheck, CreditCard, School, FileQuestion, Network,
  Landmark, Settings, ScrollText, Mail, MessageSquare, History, KeyRound, UserCog, LogOut, Menu, FileText,
} from "lucide-react";
import Logo from "@/components/brand/Logo";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  { title: "Operations", items: [["/admin", "Overview", LayoutDashboard, true], ["/admin/create-account", "Create account", UserPlus], ["/admin/parents", "Parents", Users], ["/admin/students", "Students", GraduationCap], ["/admin/consents", "Consents", ShieldCheck], ["/admin/payments", "Payments", CreditCard], ["/admin/reports", "Reports", FileText], ["/admin/schools", "Partner schools", School]] },
  { title: "Content", items: [["/admin/question-bank", "Question bank", FileQuestion], ["/admin/career-clusters", "Career clusters", Network], ["/admin/higher-education", "Higher education", Landmark], ["/admin/consent-versions", "Consent versions", ScrollText]] },
  { title: "System", items: [["/admin/settings", "Assessment settings", Settings], ["/admin/emails", "Email log", Mail], ["/admin/messages", "Messages", MessageSquare], ["/admin/audit", "Audit log", History]] },
];

function Nav({ onNavigate }) {
  const { user } = useAuth();
  const groups = [...GROUPS, { title: "Me", items: [...(user?.role === "superadmin" ? [["/admin/admins", "Admin accounts", KeyRound]] : []), ["/admin/account", "My account", UserCog]] }];
  const cls = ({ isActive }) => `flex items-center gap-3 rounded-xl px-3 h-10 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/65 hover:text-white hover:bg-white/5"}`;
  return (
    <nav className="space-y-6">
      {groups.map((g) => (
        <div key={g.title}>
          <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">{g.title}</p>
          <div className="space-y-0.5">
            {g.items.map(([to, label, Icon, end]) => (
              <NavLink key={to} to={to} end={end} onClick={onNavigate} className={cls} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SidebarBody({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col bg-[#0D040C] text-white p-4">
      <div className="px-2 py-3"><Logo light to="/admin" testId="admin-logo-link" /></div>
      <div className="mt-6 flex-1 overflow-y-auto pr-1"><Nav onNavigate={onNavigate} /></div>
      <div className="mt-4 border-t border-white/10 pt-4 px-2">
        <p className="text-sm font-semibold truncate">{user?.name}</p>
        <p className="text-xs text-white/50 truncate">{user?.email} - {user?.role}</p>
        <button onClick={async () => { await logout(); navigate("/login"); }} className="mt-3 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Sign out</button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FAF8FB]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30"><SidebarBody /></aside>
      <header className="lg:hidden sticky top-0 z-30 h-14 bg-[#0D040C] text-white flex items-center justify-between px-4">
        <Logo light to="/admin" testId="admin-mobile-logo" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button className="p-2" aria-label="Open admin menu" data-testid="admin-mobile-menu-button"><Menu className="h-6 w-6" /></button></SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-0 bg-[#0D040C]"><SheetTitle className="sr-only">Admin menu</SheetTitle><SidebarBody onNavigate={() => setOpen(false)} /></SheetContent>
        </Sheet>
      </header>
      <main className="lg:pl-64"><div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10"><Outlet /></div></main>
    </div>
  );
}
